import { isDefined, isNonEmpty } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { memo, useEffect, useState } from 'react';

import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { ExecutionStatus } from '@/App/tiles/shared/ExecutionStatus.tsx';
import { NDSSwapFields } from '@/App/tiles/Swap/NDSSwapFields.tsx';
import { SwapCommercialMargin } from '@/App/tiles/Swap/SwapCommercialMargin.tsx';
import { useSwapIsChecking, useSwapSelector } from '@/App/tiles/Swap/SwapContext.tsx';
import { SwapDetailPrices } from '@/App/tiles/Swap/SwapDetailPrices.tsx';
import { SwapDoddFranckMidPrice } from '@/App/tiles/Swap/SwapDoddFranckMidPrice.tsx';
import { SwapFooter } from '@/App/tiles/Swap/SwapFooter.tsx';
import { SwapHeader } from '@/App/tiles/Swap/SwapHeader.tsx';
import { SwapMainFields } from '@/App/tiles/Swap/SwapMainFields.tsx';
import { SwapRates } from '@/App/tiles/Swap/SwapRates.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { resetSwap } from '@/store/swap/thunks/resetSwap.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export type SwapTileProps = {
  tileId: string;
};

/**
 * Memoized SwapTile component because the Grid rerenders a lot.
 */
export const SwapTile = memo((props: SwapTileProps) => {
  const { tileId } = props;
  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  const { currencyPair, isHistoricalRate, isNonDeliverable, errorCode, executionStatus } = useSwapSelector((swap) => {
    return {
      currencyPair: swap.properties.currencyPair,
      isHistoricalRate: swap.isNonDeliverable ? false : swap.properties.type === 'historical',
      isNonDeliverable: swap.isNonDeliverable,
      errorCode:
        isDefined(swap.properties.error) && swap.properties.error.type === 'tile'
          ? swap.properties.error.code
          : undefined,
      executionStatus: swap?.execution?.status,
    };
  });

  const isChecking = useSwapIsChecking();

  const [isCommercialMarginVisible, setCommercialMarginVisible] = useState(false);

  const toggleCommercialMarginVisibility = () => {
    sgwtWebAnalytics?.trackEvent(
      'Swap',
      'Commercial margin visibility toggled',
      isCommercialMarginVisible ? 'Hidden' : 'Visible',
    );

    return setCommercialMarginVisible((isPreviouslyVisible) => !isPreviouslyVisible);
  };

  const resetTile = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile reseted', tileId);
    dispatch(resetSwap(tileId));
  };

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (isDefined(errorCode)) {
    return <ErrorTile tileId={tileId} onReset={resetTile} errorCode={errorCode} quoteId={tileId} />;
  }

  if (isDefined(executionStatus)) {
    return <ExecutionStatus status={executionStatus} />;
  }

  return (
    <AutoAdvanceForm className="d-flex bg-transparent w-100 h-100 relative">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <SwapHeader tileId={tileId} />

        {isNonEmpty(currencyPair) && (
          <>
            <SwapRates tileId={tileId} />
            <SwapDoddFranckMidPrice tileId={tileId} />
            <SwapMainFields tileId={tileId} />
            <SwapDetailPrices tileId={tileId} />

            {!isHistoricalRate && (
              <SwapCommercialMargin
                tileId={tileId}
                isCommercialMarginVisible={isCommercialMarginVisible}
                onCommercialMarginVisibilityToggle={toggleCommercialMarginVisibility}
              />
            )}

            {isNonDeliverable && <NDSSwapFields tileId={tileId} />}

            <Spacer />

            <SwapFooter tileId={tileId} />
          </>
        )}
      </VStack>

      {isChecking && (
        <div
          data-e2e="swap-tc-checking"
          className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75"
        >
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
});
